import {
  CountryCode,
  NationalNumber,
  PhoneNumber,
  getCountryCodeFromPhoneNumber,
  getDialingCodeFromCountryCode,
  getNationalNumber,
  generatePlaceholderNumber,
  parseIncompletePhoneNumber,
  countriesInfo,
} from '@choco-admin/utils';
import { Input as HeroInput, Select, SelectItem } from '@heroui/react';
import React, { useEffect, useMemo, useState } from 'react';

const Country: React.FC<{
  country: {
    code: CountryCode;
    unicode: string;
    name: string;
    dialingCode: string;
  };
}> = ({ country }) => {
  const flag = String.fromCodePoint(
    ...country.unicode.split('-').map((hex) => parseInt(hex, 16))
  );

  return (
    <div className="flex items-center gap-2">
      <span className="text-lg">{flag}</span>
      <span className="font-mono text-sm">{country.dialingCode}</span>
    </div>
  );
};

// Dropdown item type equivalent
type DropdownItem<T> = {
  key: T;
  content: React.ReactNode;
  triggerContent: string;
};

export interface PhoneInputProps {
  /** Input field ID */
  id: string;
  /** Input field name */
  name?: string;
  /** Input field label */
  label: string;
  /** Helper text to display */
  helperText?: React.ReactNode;
  /** Current phone number value */
  value?: string;
  /** Change handler */
  onChange?: (phoneNumber: PhoneNumber) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Error message */
  errorMessage?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is invalid */
  isInvalid?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Country code */
  countryCode?: CountryCode;
  /** Country code change handler */
  onChangeCountryCode?: (countryCode: CountryCode) => void;
  /** Auto focus */
  autoFocus?: boolean;
  /** Data test id */
  'data-testid'?: string;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  id,
  name = 'phone',
  label,
  helperText,
  onChange,
  placeholder,
  value = '',
  errorMessage,
  required = false,
  disabled = false,
  isInvalid = false,
  className,
  countryCode: externalCountryCode,
  onChangeCountryCode,
  autoFocus = false,
  'data-testid': dataTestId,
}) => {
  const phone = value as PhoneNumber;

  // Use external country code if provided, otherwise detect from phone
  const [internalCountryCode, setInternalCountryCode] = useState<CountryCode>(
    externalCountryCode || getCountryCodeFromPhoneNumber(phone) || 'US'
  );

  const countryCode = externalCountryCode || internalCountryCode;

  const [nationalNumber, setNationalNumber] = useState<NationalNumber>(
    (getNationalNumber(phone) ||
      phone.replace(/^\+\d+/, '').trim()) as NationalNumber
  );

  useEffect(() => {
    if (!phone) {
      setNationalNumber('' as NationalNumber);
      return;
    }

    // If phone already starts with +, it's a full international number
    if (phone.startsWith('+')) {
      const detectedCountryCode = getCountryCodeFromPhoneNumber(phone);
      const detectedNationalNumber = getNationalNumber(phone);

      // Only update country code if it's different from current
      if (detectedCountryCode && detectedCountryCode !== countryCode) {
        if (!externalCountryCode) {
          setInternalCountryCode(detectedCountryCode);
        } else if (onChangeCountryCode) {
          // If there's external control, notify parent of detected country code
          onChangeCountryCode(detectedCountryCode);
        }
      }

      // Update national number - use detected national number or fallback
      if (detectedNationalNumber) {
        setNationalNumber(detectedNationalNumber);
      } else {
        // Fallback: remove country code manually
        const withoutCountryCode = phone.replace(/^\+\d{1,4}\s?/, '').trim();
        setNationalNumber(withoutCountryCode as NationalNumber);
      }
    } else {
      // It's already a national number, use as-is
      setNationalNumber(phone as NationalNumber);
    }
  }, [phone, externalCountryCode, countryCode]);

  // Create countries options with pre-calculated flags
  const countriesOptions: DropdownItem<CountryCode>[] = useMemo(() => {
    return Object.values(countriesInfo).map((country) => ({
      key: country.code,
      content: <Country country={country} />,
      triggerContent: country.dialingCode,
    }));
  }, []);

  const updatePhoneNumberFields = (
    newCountryCode: CountryCode,
    rawPhoneNumber: string,
    options = { format: false }
  ) => {
    if (options.format && rawPhoneNumber.trim()) {
      // If the raw phone number already starts with +, it's a complete international number
      if (rawPhoneNumber.startsWith('+')) {
        // Extract country code and national number from the full international number
        const detectedCountryCode =
          getCountryCodeFromPhoneNumber(rawPhoneNumber);
        const detectedNationalNumber = getNationalNumber(rawPhoneNumber);

        if (detectedCountryCode && detectedNationalNumber) {
          // Update country code
          if (onChangeCountryCode) {
            onChangeCountryCode(detectedCountryCode);
          } else {
            setInternalCountryCode(detectedCountryCode);
          }

          setNationalNumber(detectedNationalNumber);

          // Use the raw phone number as-is (it's already complete) but remove spaces
          onChange?.(rawPhoneNumber.replace(/\s/g, '') as PhoneNumber);
        } else {
          // Fallback if parsing fails
          onChange?.(rawPhoneNumber.replace(/\s/g, '') as PhoneNumber);
        }
      } else {
        // National number - parse and format normally
        const parsedPhoneNumber = parseIncompletePhoneNumber(
          rawPhoneNumber,
          newCountryCode
        );

        const newPhoneNumber = parsedPhoneNumber.phoneNumber;

        // Update country code
        if (onChangeCountryCode) {
          onChangeCountryCode(parsedPhoneNumber.countryCode);
        } else {
          setInternalCountryCode(parsedPhoneNumber.countryCode);
        }

        setNationalNumber(newPhoneNumber as NationalNumber);

        // Create full phone number - use the international format without spaces
        const dialingCode = getDialingCodeFromCountryCode(
          parsedPhoneNumber.countryCode
        );
        const fullPhoneNumber = dialingCode + newPhoneNumber.replace(/\s/g, '');
        onChange?.(fullPhoneNumber as PhoneNumber);
      }
    } else {
      // Immediate update without parsing for performance
      setNationalNumber(rawPhoneNumber as NationalNumber);

      // Handle empty input
      if (!rawPhoneNumber.trim()) {
        onChange?.('');
      } else {
        // Only add dialing code if the raw number doesn't already start with +
        let fullPhoneNumber: string;
        if (rawPhoneNumber.startsWith('+')) {
          // Raw number already has country code, remove spaces if any
          fullPhoneNumber = rawPhoneNumber.replace(/\s/g, '');
        } else {
          // Add country code to national number
          const dialingCode = getDialingCodeFromCountryCode(newCountryCode);
          fullPhoneNumber = dialingCode + rawPhoneNumber.replace(/\s/g, '');
        }
        onChange?.(fullPhoneNumber as PhoneNumber);
      }
    }
  };

  const onCountryCodeChange = (code: CountryCode) => {
    // Update internal state if no external handler
    if (!onChangeCountryCode) {
      setInternalCountryCode(code);
    } else {
      onChangeCountryCode(code);
    }

    // Always update the phone number when country changes
    updatePhoneNumberFields(code, nationalNumber, { format: true });
  };

  const onPhoneNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value;

    // Phone format-as-you-type only works if the user is editing the end of the string
    const shouldFormat = e.currentTarget.selectionStart === inputValue.length;
    updatePhoneNumberFields(countryCode, inputValue, {
      format: shouldFormat,
    });
  };

  const onPhoneNumberBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value;
    updatePhoneNumberFields(countryCode, inputValue, {
      format: true,
    });
  };

  const exampleNumber = useMemo(
    () => generatePlaceholderNumber(countryCode),
    [countryCode]
  );

  const selectedOption = countriesOptions.find(
    (opt) => opt.key === countryCode
  );

  return (
    <div className={`space-y-2 ${className || ''}`} data-testid={dataTestId}>
      {/* Label */}
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
        {required && <span className="text-danger ml-1">*</span>}
      </label>

      {/* Helper Text */}
      {helperText && (
        <div className="text-sm text-default-500">{helperText}</div>
      )}

      <div className="flex gap-2">
        <div className="w-32">
          <Select
            id={`${id}-country`}
            selectedKeys={[countryCode]}
            onSelectionChange={(keys) => {
              const keyArray = Array.from(keys);
              if (keyArray.length > 0) {
                const newCode = keyArray[0] as CountryCode;
                onCountryCodeChange(newCode);
              }
            }}
            placeholder="🌍"
            variant="bordered"
            isDisabled={disabled}
            aria-label="Select country"
            renderValue={() => {
              const flag = selectedOption
                ? String.fromCodePoint(
                    ...countriesInfo[selectedOption.key].unicode
                      .split('-')
                      .map((hex) => parseInt(hex, 16))
                  )
                : '🌍';

              return (
                <div className="flex items-center gap-1">
                  <span className="text-lg">{flag}</span>
                  <span className="text-xs font-mono">
                    {selectedOption?.triggerContent}
                  </span>
                </div>
              );
            }}
            classNames={{
              trigger:
                'bg-default-50 border-default-200 hover:border-default-300 focus-within:border-primary px-2 py-2 shadow-sm hover:shadow-md transition-all duration-200 min-h-10',
              innerWrapper: 'pr-8', // Increased padding to ensure proper caret spacing
              value: 'text-foreground text-sm',
              selectorIcon: 'right-2',
              popoverContent: 'shadow-lg border border-default-200 w-48',
            }}
          >
            {countriesOptions.map((option) => {
              const country = countriesInfo[option.key];
              const textValue = `${country.name} ${country.dialingCode}`;
              return (
                <SelectItem key={option.key} textValue={textValue}>
                  {option.content}
                </SelectItem>
              );
            })}
          </Select>
        </div>

        {/* Phone Number Input */}
        <div className="flex-1">
          <HeroInput
            id={id}
            name={name}
            value={nationalNumber}
            onChange={onPhoneNumberInputChange}
            onBlur={onPhoneNumberBlur}
            placeholder={placeholder || exampleNumber}
            variant="bordered"
            isInvalid={isInvalid || !!errorMessage}
            isDisabled={disabled}
            autoFocus={autoFocus}
            classNames={{
              input: 'text-foreground bg-transparent px-3 py-2 text-sm',
              inputWrapper:
                'bg-default-50 border-default-200 hover:border-default-300 focus-within:border-primary shadow-sm hover:shadow-md transition-all duration-200',
            }}
          />
        </div>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="text-sm text-danger">{errorMessage}</div>
      )}
    </div>
  );
};
